// src/components/GradQuickAccess.jsx
// A floating quick-access button for Graduation Day staff — one tap to the
// dashboard or the check-in scanner, from anywhere in the app.
//
// Why this exists rather than just more nav links: the mobile bottom tab
// bar already carries 6 items for a portal user and caps at 5 before
// folding the rest behind "More", so anything appended there lands in an
// overflow sheet — two taps, not one, on exactly the screen (a phone at
// the gate) where speed matters most. The check-in scanner in particular
// wasn't reachable from the portal nav AT ALL (it's only listed in
// ADMIN_NAV/ADMIN_TABS), so a coordinator who isn't also a hall incharge
// or superadmin had no link to it anywhere — they'd have to type the URL.
//
// Visibility is deliberately narrow:
//  - only for users gradApi.getMyAccess() confirms are actual Graduation
//    Day team members (same gate Shell.jsx's nav items use) — never shown
//    to a random portal user
//  - hidden on /grad/* pages themselves, where it'd just point at where
//    you already are
//  - hidden on the public, no-login pages (/grad/confirm, /grad/guardian,
//    /grad/display), which students, guardians and the kiosk display use —
//    they aren't staff and the kiosk needs its screen uncluttered
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { GraduationCap, QrCode, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { gradApi } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function GradQuickAccess() {
  const { user, isSuperAdmin } = useAuth();
  const loc = useLocation();
  const [access, setAccess] = useState(null);
  const [open, setOpen] = useState(false);

  // Same access check Shell.jsx runs for its own grad nav items, and same
  // failure posture: any error resolves to "no access" rather than
  // showing staff-only shortcuts by default.
  //
  // Skipped entirely on /grad/* pages: the button returns null there
  // anyway (see below), and those pages run their own getMyAccess() call.
  // Without this guard, opening the dashboard or scanner would fire a
  // third identical request for a button that was never going to render.
  const onGradPage = loc.pathname.startsWith("/grad/");
  useEffect(() => {
    if (onGradPage) return;
    if (!user) { setAccess({ allowed: false }); return; }
    if (isSuperAdmin) { setAccess({ allowed: true }); return; }
    let cancelled = false;
    gradApi.getMyAccess()
      .then((r) => { if (!cancelled) setAccess(r?.success ? r : { allowed: false }); })
      .catch(() => { if (!cancelled) setAccess({ allowed: false }); });
    return () => { cancelled = true; };
  }, [user, isSuperAdmin, onGradPage]);

  // Collapse the expanded state on navigation — otherwise it stays open
  // over the page you just moved to.
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  if (onGradPage) return null;
  if (!access?.allowed) return null;

  const items = [
    { to: "/grad/dashboard", label: "Graduation Day", icon: GraduationCap },
    { to: "/grad/scan", label: "Grad Check-in", icon: QrCode },
  ];

  return (
    // Sits above the mobile tab bar (60px + the device's own safe-area
    // inset) so it never covers a tab. The same offset is kept on desktop,
    // where there's no tab bar — it just floats slightly higher in the
    // corner, which is harmless and avoids a responsive override that an
    // inline style would silently win over anyway. z-[80] keeps it
    // deliberately BELOW PwaBanners (z-[90]/z-[100]) — if an install or
    // update banner is ever on screen at the same time, that banner should
    // win rather than be obscured by a shortcut.
    <div
      className="fixed right-4 z-[80] flex flex-col items-end gap-2"
      style={{ bottom: "calc(60px + env(safe-area-inset-bottom, 0px) + 16px)" }}
    >
      {open && items.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            className="flex items-center gap-2 bg-white border shadow-lg rounded-full pl-3.5 pr-5 py-2.5 text-sm font-medium hover:bg-slate-50"
          >
            <Icon className="w-5 h-5 text-primary shrink-0" />
            <span className="whitespace-nowrap">{item.label}</span>
          </Link>
        );
      })}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close Graduation Day shortcuts" : "Graduation Day shortcuts"}
        aria-expanded={open}
        className={cn(
          // Bumped from w-12 h-12 (48px) to w-16 h-16 (64px) — the
          // previous size read as too small to notice at a glance,
          // especially against a busy background. Icon scaled up to
          // match (w-5 -> w-7) rather than leaving it looking small
          // inside a now-bigger circle.
          "w-16 h-16 rounded-full shadow-lg flex items-center justify-center transition-colors",
          open ? "bg-slate-900 text-white" : "bg-primary text-white hover:bg-primary/90"
        )}
      >
        {open ? <X className="w-7 h-7" /> : <GraduationCap className="w-7 h-7" />}
      </button>
    </div>
  );
}
